<script lang="ts">
// this must be exported here for the keep alive on reroute
export default {
  name: "Children",
};
</script>

<script setup lang="ts">
import ChildrenTable from "@/components/models/child/ChildTable.vue";
import { useSnackStore } from "@/stores/snack";
import { useQuery } from "@tanstack/vue-query";
import { definePage } from "unplugin-vue-router/runtime";
import { onMounted, provide, ref } from "vue";

import ChildService, { type TChild } from "@/api/child";
import DeviceService from "@/api/device";
import { ActionsEnum, SubjectsEnum, type AppAbility } from "@/services/ability";
import { generateFormOptions, type PaginatedType, type VFormFieldChoice, type VFormOptions } from "@/utils/DjangoTypes";
import { DefaultServerTableParams, type ServerTableParamsType } from "@/utils/TableUtils";
import { useAbility } from "@casl/vue";

definePage({
  meta: {
    title: "Children",
    checkPrivileges: () => {
      const { can } = useAbility<AppAbility>();
      return can(ActionsEnum.USE, SubjectsEnum.CHILD);
    },
  },
});

const snackStore = useSnackStore();
const fetchEnabled = ref(false);

const options = ref<VFormOptions>({});
const devicesChoices = ref<VFormFieldChoice<any>[]>([]);

const queriesTableParams: Record<string, ServerTableParamsType> = {
  children: DefaultServerTableParams,
};
provide("queriesTableParams", queriesTableParams);

const {
  data: children,
  isPending: isPending,
  isFetching: isFetching,
} = useQuery({
  queryKey: ["children"],
  queryFn: async (): Promise<PaginatedType<TChild[]> | undefined> => {
    try {
      const [result, restOptions, devices] = await Promise.all([
        ChildService.child.getAll(queriesTableParams.children),
        ChildService.child.getOptions(),
        DeviceService.device.getAllSelectable(),
      ]);

      options.value = generateFormOptions(restOptions.data.actions.POST);

      devicesChoices.value = devices;

      return result.data;
    } catch (error) {
      snackStore.showError(error);
    }
  },
  enabled: () => fetchEnabled.value,
});

onMounted(async () => {
  fetchEnabled.value = true;
});
</script>

<template>
  <v-container fluid>
    <div class="tw:!mb-10">
      <children-table
        :query-key="'children'"
        :data="children"
        :loading="isPending || isFetching"
        :devices="devicesChoices"
        :options
      />
    </div>
  </v-container>
</template>

<style scoped></style>
